import React, { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { LogIn, ShieldAlert } from 'lucide-react';
import FormInput from '../../components/FormInput';
import LoginOtpStep from '../../components/LoginOtpStep';
import { useAuth } from '../../hooks/useAuth';
import { errorMessage } from '../../api/client';
import { BRAND_NAME } from '../../utils/brand';

// Soft client-side gate. The real authority is the backend, which compares
// the submitted code against SUPER_ADMIN_ACCESS_CODE (env var). Keeping a
// fallback here lets the gate work in local dev without extra setup.
const EXPECTED_CODE =
  process.env.REACT_APP_SUPER_ADMIN_CODE || 'SUPER2025';

function SuperAdminLogin() {
  const { signInSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const code = params.get('code') || '';

  const from = new URLSearchParams(location.search).get('from') || '/dashboard';
  const codeOk = code === EXPECTED_CODE;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [otpChallenge, setOtpChallenge] = useState(null);

  async function onSubmit(e) {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const data = await signInSuperAdmin(email, password, code);
      if (data?.requiresOtp) {
        setOtpChallenge(data);
        toast.success(`Verification code sent to ${data.to}`);
      } else {
        toast.success('Super-admin signed in');
        navigate(from, { replace: true });
      }
    } catch (e1) {
      setErr(errorMessage(e1));
    } finally {
      setBusy(false);
    }
  }

  if (!codeOk) {
    return (
      <div className="auth-card">
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--s-4)' }}>
          <img src="/fullLogo.png" alt={BRAND_NAME} style={{ height: 48, width: 'auto' }} draggable="false" />
        </div>
        <div className="notice warning row" style={{ gap: 8, alignItems: 'flex-start' }}>
          <ShieldAlert size={18} />
          <div>
            <strong>Restricted area.</strong>
          </div>
        </div>
        <div className="divider" />
        <div className="text-sm muted" style={{ textAlign: 'center' }}>
          <Link to="/login">Back to standard sign in</Link>
        </div>
      </div>
    );
  }

  if (otpChallenge) {
    return (
      <div className="auth-card">
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--s-4)' }}>
          <img src="/fullLogo.png" alt={BRAND_NAME} style={{ height: 48, width: 'auto' }} draggable="false" />
        </div>
        <LoginOtpStep
          challenge={otpChallenge}
          onSuccess={() => { toast.success('Super-admin signed in'); navigate(from, { replace: true }); }}
          onBack={() => { setOtpChallenge(null); setErr(''); }}
          onResend={async () => {
            const data = await signInSuperAdmin(email, password, code);
            if (data?.requiresOtp) setOtpChallenge(data);
          }}
        />
      </div>
    );
  }

  return (
    <div className="auth-card">
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--s-4)' }}>
        <img src="/fullLogo.png" alt={BRAND_NAME} style={{ height: 48, width: 'auto' }} draggable="false" />
      </div>
      <h2>Super-admin sign in</h2>
      <div className="auth-sub">Access code verified. Enter your credentials.</div>

      <form onSubmit={onSubmit}>
        <FormInput label="Email" type="email" autoComplete="email"
          value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="superadmin@mydoctorai.com" required />
        <FormInput label="Password" type="password" autoComplete="current-password"
          value={password} onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••" required />

        {err && <div className="notice warning" style={{ marginBottom: 'var(--s-4)' }}>{err}</div>}

        <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
          {busy ? <span className="spinner" /> : <LogIn size={16} />}
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      <div className="divider" />
      <div className="text-sm muted" style={{ textAlign: 'center' }}>
        <Link to="/login">Standard sign in</Link>
      </div>
    </div>
  );
}

export default SuperAdminLogin;
